// Every typography variant must carry the SAME fallback stack. A bare
// `fontFamily: "Open Sans"` falls back to the browser default (Times) when the
// webfont is unavailable -- which is why headings went serif while body copy,
// which had the full stack, stayed sans.
const fontStack = [
	"Open Sans",
	"-apple-system",
	"BlinkMacSystemFont",
	'"Segoe UI"',
	'"Helvetica Neue"',
	"Arial",
	"sans-serif",
	'"Apple Color Emoji"',
	'"Segoe UI Emoji"',
	'"Segoe UI Symbol"',
].join(",");

const themeConfig = {
	shape: {
		borderRadius: 3,
	},
	breakpoints: {
		keys: ["xxs", "xs", "sm", "md", "lg", "xl", "xxl"],
		values: {
			xxs: 0,
			xs: 620,
			sm: 800,
			md: 1100,
			lg: 1270,
			xl: 1420,
			xxl: 1536,
		},
		unit: "px",
	},
	palette: {
		body: {
			light: "#f5f5f5",
			main: "#f5f5f5",
			dark: "#e0e0e0",
			contrastText: "#000000",
		},
		dark: {
			main: "rgb(0, 0, 0)",
			light: "rgb(20, 20, 20)",
			dark: "rgb(0, 0, 0)",
			contrastText: "#ffffff",
		},
		appBar: {
			public: {
				main: "#FFFFFF",
				light: "#FFFFFF",
				dark: "#FFFFFF",
				contrastText: "#2C2C6D",
			},
		},
		titleBar: {
			public: {
				main: "#2C2C6D",
				light: "#2C2C6D",
				dark: "#2C2C6D",
				contrastText: "#ffffff",
				gradient: `url('/texture.png'), linear-gradient(90deg, #2b2c6d 30%, #0d8bb1)`,
			},
		},
		primary: {
			main: "#2C2C6D",
			light: "#2C2C6D",
			dark: "#2C2C6D",
		},
		secondary: {
			main: "#B8A57A",
			light: "#B8A57A",
			dark: "#B8A57A",
		},
		underline: {
			main: "#B8A57A",
		},
		headings: {
			main: "#263238",
		},
		footer: {
			main: "#1E1E1E",
			light: "#1E1E1E",
			dark: "#1E1E1E",
			contrastText: "#ffffff",
		},
		colophon: {
			main: "#1E1E1E",
			light: "#1E1E1E",
			dark: "#1E1E1E",
			contrastText: "#ffffff",
		},
		link: {
			main: "#016197",
			light: "#0472b0",
			dark: "#00466e",
		},
		excerptLink: {
			main: "#000000",
			light: "#000000",
			dark: "#000000",
		},
		tab: {
			paper: "#eeeeee",
			main: "#2C2C6D",
			hover: "#ffffff",
			contrastTextHover: "#2C2C6D",
			contrastText: "#ffffff",
		},
		divider: "rgba(0, 0, 0, 0.12)",
		subnav: {
			main: "#ECEFF1",
		},
	},
	typography: {
		fontFamily: fontStack,
		subtitle2: {
			fontFamily: fontStack,
			fontWeight: 600,
			opacity: 1,
			color: "#000000",
		},
		body1: {
			fontSize: "1em",
		},
		body2: {
			opacity: 1,
		},
		h1: {
			fontFamily: fontStack,
			fontWeight: 400,
			fontSize: "2rem",
		},
		h2: {
			fontFamily: fontStack,
			fontWeight: 600,
			fontSize: "1.8rem",
		},
		h3: {
			fontFamily: fontStack,
			fontWeight: 600,
			fontSize: "1.6rem",
		},
		h4: {
			fontFamily: fontStack,
			fontWeight: 600,
			fontSize: "1.4rem",
		},
		h5: {
			fontFamily: fontStack,
			fontWeight: 600,
			fontSize: "1.2rem",
		},
		h6: {
			fontFamily: fontStack,
			fontWeight: 600,
			fontSize: "1rem",
		},
		brandTitle: {
			fontFamily: fontStack,
			fontWeight: 600,
			fontSize: "1rem",
		},
		fontWeightBold: 600,
		titleBar: {
			fontFamily: fontStack,
		},
		subtitle1: {
			fontStyle: "normal",
			fontWeight: 400,
			lineHeight: 1.5,
			letterSpacing: "-0.01562em",
			fontSize: "1.3rem",
		},
		nav: {
			fontFamily: fontStack,
			textTransform: "uppercase",
			fontSize: "0.9rem",
			fontWeight: 400,
			paddingTop: "1rem",
		},
		subnav: {
			textTransform: "uppercase",
		},
	},
	cloverleaf: {
		googleFont: "Open+Sans:ital,wght@0,400;0,600;1,400;1,600",
		closingHeadingsStyled: false,
		brand: {
			size: 104,
			paddingTop: 0.001,
			paddingBottom: 0.001,
			logoFooter: "/logo-footer.jpg",
			logoFooterSize: 400,
		},
		list: {
			underline: "#B8A57A",
			underlineStyle: "rounded",
		},
		single: {
			style: "flush",
			containerStyle: "div",
		},
		navbar: {
			align: "right",
			borderRadius: 0,
		},
		sidebar: {
			style: "box",
			underline: "#B8A57A",
		},
		tab: {
			// Accordion (Tab) summary title, matched to body copy.
			// MUI renders AccordionSummary as a <button>, which the UA stylesheet
			// sets to 13.33px, and this site's body1 is "1em" -- relative -- so an
			// unset title resolves to 1em of 13.33px and comes out SMALLER than body
			// text rather than equal to it. 1rem is absolute, so it lands on the same
			// 16px as the accordion body and the page body.
			fontSize: "1rem",
		},
		components: {
			More: {
				variant: "text",
				color: "primary",
			},
		},
		css: `
			/* Chapter list on a publication page. MUI's CssBaseline strips list
			   styling from ul/li, so the markers have to be re-declared here. */
			#___gatsby .chapter-list {
				list-style: disc outside;
				margin: 0;
				padding-left: 1.5rem;
			}

			#___gatsby .chapter-list .chapter-list-item {
				display: list-item;
				padding: 0;
				margin-bottom: 0.5rem;
			}

			html.is-public body {
				background-color: #333d47;
				color: #000000;
			}

			html.is-public .desktop-brand .brand.as-logo > span {
				height: 140px !important;
				padding-top: 18px !important;
				padding-bottom: 18px !important;
			}

			#___gatsby .desktop-nav {
				position: relative;
			}

			#___gatsby .desktop-nav::before {
				content: " ";
				position: absolute;
				top: -2px;
				right: 0;
				width: 130%;
				height: 1px;
				background-image: -webkit-gradient(
					linear,
					left top,
					right top,
					color-stop(28%, rgba(255, 255, 255, 0.01)),
					to(#2c2c6d)
				);
				background-image: linear-gradient(
					90deg,
					rgba(255, 255, 255, 0.01) 28%,
					#2c2c6d
				);
				z-index: 1;
			}


			/* #___gatsby .call-to-action.layout-bottom {
			background-image: url('/texture3.png?v=1');
			background-size: auto 100%;
			} */

			#___gatsby .call-to-action.layout-bottom .filter-1 {
				opacity: 0.5;
			}

			#___gatsby .call-to-action.layout-bottom .filter-2 {
				opacity: 0.4;
			}

			#___gatsby .call-to-action.layout-bottom .filter-3 {
				opacity: 0.5;
			}

			#footer .MuiGrid-container .MuiGrid-item {
				padding-top: 20px !important;
			}

			#footer .MuiGrid-container .MuiGrid-item:first-child .text > * {
				margin-bottom: 15px;
			}

			#footer .MuiGrid-container .MuiGrid-item img[src*="logo"] {
				height: 53px;
				width: auto;
				margin-right: 30px;
			}

			#footer .MuiGrid-container img[src*="twitter"] {
				height: 24px;
				width: auto;
			}

			#footer .colophon {
				padding-top: 15px;
				margin-top: 15px;
			}

			#___gatsby #single-body .closing-section {
				padding-bottom: 40px;
			}

			#___gatsby #single-body .closing-section .image-block.square {
				padding-top: 80%;
			}

			#___gatsby #single-body .closing-section > .MuiContainer-root {
				margin-top: 80px;
				max-width: 1200px;
			}

			#___gatsby #single-body .closing-section > .title-bar + .MuiContainer-root {
				margin-top: 40px;
			}

			#___gatsby #single-body .closing-section > .title-bar {
				margin-top: 80px;
			}

			#___gatsby #single-body .closing-section .highlight.style-frame .inner {
				padding: 30px 24px;
			}

			.sidebar-section .MuiPaper-root > *:not(:first-child) {
				margin-top: 15px;
			}

			.main-container {
				padding-bottom: 60px !important;
			}

			#___gatsby .MuiAccordionSummary-expandIconWrapper {
				color: #ffffff;
			}

			html.type-node--personnel .main-section h2,
			html.type-node--personnel .main-section .heading-h2 {
				font-size: 1.4rem;
				margin-top: 50px !important;
			}

			html.type-node--personnel .main-section .text blockquote {
				margin-left: 0;
			}

			html.type-node--event .MuiTypography-h1 {
				font-weight: 600;
				margin-top: 30px;
			}

			html.type-node--event time.MuiTypography-overline {
				font-size: 1.3rem;
				font-weight: 400;
				line-height: 1.5;
				text-transform: none;
			}

			html.type-node--fellowship .MuiAccordionDetails-root {
				padding: 36px;
			}
			html.type-node--fellowship .MuiAccordionSummary-root .MuiTypography-root {
				font-size: 1.2rem;
				font-weight: 600;
			}

			html.type-node--fellowship .MuiAccordionSummary-root {
				background-color: #d4d4d4;
				color: #242424;
			}

			body .text blockquote {
				background-color: #f1f6fb;
				border-left: 16px solid #e9eff5;
				font-size: 1.3em;
				padding: 32px 40px;
				margin: 2% 0;
			}

			.main-section .MuiAvatar-root {
				transform: scale(1.25);
			}

			.excerpt-content h3 {
				padding-top: 5px;
			}

			.type-node--page .entities-list .excerpt-content .subtitle {
				font-weight: bold;
			}

			.desktop-nav-wrap.MuiBox-root > .tools-nav.MuiBox-root {
				border-bottom: 1px solid;
				border-image: linear-gradient(to right, #ffffff, #2c2c6d) 1 100;
			}

			.closing-section.MuiBox-root > .cta-wrap.MuiBox-root {
				background-position: 0% 70%;
			}

			.cta-wrap.MuiBox-root > div.call-to-action {
				border-top: 4px solid #b8a57a;
				margin-top: 10rem;
				padding-top: 24px;
				padding-bottom: 24px;

				background-color: #2b2c6d;
				background-image: url(./20220121_W-in-library_019.jpeg);
				background-position: 0 90%;
				background-repeat: no-repeat;
				background-size: cover;
			}

			.cta-wrap.MuiBox-root > div.call-to-action > div.MuiContainer-root > div > div {
				padding-left: unset;
				padding-right: unset;
				max-width: unset;
			}

			#single-body > main > .closing-section.MuiBox-root a,
			#single-body > main > .closing-section.MuiBox-root button {
				border-radius: unset;
				text-align: start;
				box-shadow: unset;
				text-transform: none;
				justify-content: start;
				padding-top: 1rem;
				padding-bottom: 1rem;
			}

			#single-body > main > .closing-section.MuiBox-root > .MuiPaper-root.title-bar {
				background-color: #242424;
				background-image: url(./texture.png), linear-gradient(to right, #2b2c6d 100%, #76236c);
				background-repeat: repeat, repeat;
				background-size: auto, auto;
			}

			.type-node--publication #___gatsby .chapter-body .title-bar header > strong {
				display: none;
			}

			.type-node--publication #___gatsby .chapter-body .titles-box h1 {
				font-weight: bold;
			}

			.type-node--publication #___gatsby .print-publication h1 {
				font-weight: bold;
				font-size: 260%;
			}

			#___gatsby .desktop-nav nav a {
				color: #000;
			}

			#___gatsby .text table *,
			#___gatsby .text #table-responsive *{
				text-align: left;
			}

			@media (min-width: 1270px) {
				.type-node--publication .main-container > .MuiContainer-root:first-of-type {
					max-width: 1270px !important;
				}
			}
			.highlight .thumbnail figure {
				padding-top: 55%;
			}
		`,
	},
};

module.exports = themeConfig;
